import { Button, FactRow, Kbd, plainMarkup, RileyChatInput, RileyRail } from '@patchbay/ui'
import { useNavigate } from '@tanstack/react-router'
import { Eye, ShieldAlert, Target } from 'lucide-react'
import * as React from 'react'
import type { CaseRow } from '../../lib/api/generated'
import { formatLaneBasis } from '../../lib/display-time'
import { flagLabel } from '../../lib/flags'
import { useWorkspace } from '../../lib/workspace'
import { ChatStream } from '../chat-items'
import { sendHomeMessage } from '../home-chat'
import { OverdueActions } from './overdue-actions'
import { askQueueRiley, queueRileyChats } from './queue-rail-store'
import { NO_SUMMARY, rowWho } from './queue-row'

const RAIL_SELECTION_DELAY_MS = 150

/**
 * Keep rapid arrow-key movement cheap: the row highlight moves immediately, while the rail settles on the last
 * selection after a short debounce. The rail uses the already-loaded queue row and never adds a context request.
 */
export function useDebouncedQueueRailRow(row: CaseRow | undefined, delay = RAIL_SELECTION_DELAY_MS): CaseRow | undefined {
  const [current, setCurrent] = React.useState(row)
  const currentId = current?.id
  React.useEffect(() => {
    if (!row) {
      setCurrent(undefined)
      return
    }
    if (!currentId || currentId === row.id) {
      setCurrent(row)
      return
    }
    const timer = window.setTimeout(() => setCurrent(row), delay)
    return () => window.clearTimeout(timer)
  }, [currentId, delay, row])
  return current
}

function QueueCaseSummary({ row }: { row: CaseRow }) {
  const summary = row.case_summary
  if (!summary) return <p>{plainMarkup(row.summary_line) || NO_SUMMARY}</p>
  const field = (label: string, value: string | null | undefined) =>
    value ? (
      <div>
        <dt className="text-2xs font-medium uppercase tracking-wide text-subtle">{label}</dt>
        <dd>{plainMarkup(value)}</dd>
      </div>
    ) : null
  return (
    <div className="space-y-2">
      <p className="font-medium text-foreground">{plainMarkup(summary.headline)}</p>
      <dl className="grid gap-2">
        {field('Issue', summary.problem)}
        {summary.tried.length ? (
          <div>
            <dt className="text-2xs font-medium uppercase tracking-wide text-subtle">Tried</dt>
            <dd>
              <ul className="list-disc space-y-0.5 pl-4">
                {summary.tried.map((item) => (
                  <li key={item}>{plainMarkup(item)}</li>
                ))}
              </ul>
            </dd>
          </div>
        ) : null}
        {field('Where it stands', summary.status)}
        {field('Waiting on', summary.waiting_on)}
      </dl>
    </div>
  )
}

export function QueueRail({
  row,
  onChase,
  onReplan,
  onSnooze,
}: {
  row: CaseRow | undefined
  onChase?: () => void
  onReplan?: () => void
  onSnooze?: () => void
}) {
  const ws = useWorkspace()
  const navigate = useNavigate()
  // Lines and the in-flight flag live outside the rail, so expanding it mid-question keeps "Asking Riley…" (#196).
  const { lines, busy } = queueRileyChats.use(row?.id ?? '')
  if (!row) {
    // No case selected: the cross-case (queue-scope) thread, never a refusal (#782).
    const askAcross = (text: string) => askQueueRiley('', text, (t) => sendHomeMessage(t))
    return (
      <RileyRail
        mode={ws.rileyMode}
        onModeChange={ws.setRileyMode}
        chat={
          <>
            {lines.length ? (
              <ChatStream items={lines} />
            ) : (
              <p className="text-sm text-muted-foreground">Ask Riley about your queue, or select a case to talk about that one.</p>
            )}
            {busy ? (
              <p role="status" className="text-xs text-muted-foreground">
                Asking Riley…
              </p>
            ) : null}
          </>
        }
        input={<RileyChatInput placeholder="Ask Riley anything about your queue…" onSubmit={(text) => void askAcross(text)} />}
      />
    )
  }
  const who = rowWho(row)
  const open = () => navigate({ to: '/case/$id', params: { id: row.number } })
  // Ask Riley here posts to the case's own thread, so Riley has this case's tools, never a fake reply (#782).
  const ask = (text: string) => askQueueRiley(row.id, text, (t) => sendHomeMessage(t, row.id))
  return (
    <RileyRail
      mode={ws.rileyMode}
      onModeChange={ws.setRileyMode}
      title={
        <Button
          size="sm"
          variant="ghost"
          className="max-w-full gap-1.5 px-2 text-riley"
          aria-label={`Peeking ${row.number}; open case`}
          title={`Open ${row.number} (Enter)`}
          onClick={open}
        >
          <Eye /> <span className="truncate">Peeking {row.number}</span> <span aria-hidden>·</span> Open <Kbd keys="enter" />
        </Button>
      }
      summary={<QueueCaseSummary row={row} />}
      context={
        <>
          <FactRow
            label="Customer"
            value={who.unresolved && row.contact_display ? `${who.text} (account not resolved)` : who.text}
            source={who.unresolved ? 'Desk contact' : 'case mirror'}
          />
          <FactRow label="Case" value={`${row.number} · ${row.title}`} source="Patchbay" />
          {row.lane_basis ? <FactRow label="Why this lane" value={formatLaneBasis(row.lane_basis)} source="Patchbay" /> : null}
          {row.overdue_waiting_party && onChase && onReplan && onSnooze ? (
            <div className="px-3.5 py-2">
              <OverdueActions
                party={row.overdue_waiting_party}
                variant="buttons"
                onChase={onChase}
                onReplan={onReplan}
                onSnooze={onSnooze}
              />
            </div>
          ) : null}
          <FactRow
            icon={<Target />}
            label="Next move"
            value={row.next_move ? `${row.next_move.text} · ${row.next_move.owner_kind}` : 'None set'}
            source="Patchbay event spine"
          />
          {row.flags && row.flags.length > 0 ? (
            <FactRow icon={<ShieldAlert />} label="Flags" value={row.flags.map(flagLabel).join(' · ')} />
          ) : null}
        </>
      }
      chat={
        <>
          {lines.length ? <ChatStream items={lines} /> : null}
          {busy ? (
            <p role="status" className="text-xs text-muted-foreground">
              Asking Riley…
            </p>
          ) : null}
        </>
      }
      input={<RileyChatInput placeholder={`Ask Riley about ${row.number}…`} onSubmit={(text) => void ask(text)} />}
      options={[
        { label: 'Open case', onSelect: open },
        {
          label: 'Clear these answers',
          onSelect: () => queueRileyChats.set(row.id, (chat) => ({ ...chat, lines: [] })),
          disabled: lines.length === 0 || busy,
        },
      ]}
    />
  )
}
